import React, { useRef } from 'react';
import { X, Link2, Download } from 'lucide-react';
import QRCode from "react-qr-code";
import {
  Dialog,
  DialogContent,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";

interface FormQRCodeModalProps {
  isOpen: boolean;
  onClose: () => void;
  deskName: string;
  formName: string;
  qrLink: string;
}

export const FormQRCodeModal: React.FC<FormQRCodeModalProps> = ({
  isOpen,
  onClose,
  deskName,
  formName,
  qrLink
}) => {
  const qrContainerRef = useRef<HTMLDivElement>(null);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(qrLink);
    toast.success("Link copied to clipboard!");
  };

  const handleDownloadQR = () => {
    if (!qrContainerRef.current) return;
    const svg = qrContainerRef.current.querySelector("svg");
    if (!svg) return;

    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx?.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `${formName.replace(/\s+/g, '_')}_QR.png`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };

    img.src = "data:image/svg+xml;base64," + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent hideClose={true} className="p-0 sm:max-w-md border-none overflow-hidden bg-white shadow-2xl rounded-2xl">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-gray-900">Form QR Code</h3>
            <button 
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-900"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex flex-col items-center space-y-4">
            <div className="w-full text-center space-y-1 mb-2">
              <div className="text-sm text-muted-foreground font-medium">{deskName}</div>
              <div className="font-bold text-lg text-gray-900">{formName}</div>
            </div>

            <div ref={qrContainerRef} className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm">
              <QRCode 
                value={qrLink} 
                size={220} 
                style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                viewBox={`0 0 256 256`}
              />
            </div>

            <div className="w-full space-y-3 pt-2">
              <p className="text-sm text-muted-foreground text-center font-medium">Scan this QR code to access the form</p>
              
              <Button 
                variant="outline"
                className="w-full flex items-center justify-center gap-2 h-11 rounded-xl border-gray-200 font-bold text-gray-700 hover:bg-gray-50 transition-all uppercase tracking-tight text-xs"
                onClick={handleCopyLink}
              >
                <Link2 className="w-4 h-4" />
                Copy Link
              </Button>

              <Button 
                className="w-full flex items-center justify-center gap-2 h-11 bg-primary text-white rounded-xl hover:opacity-90 transition-all shadow-lg shadow-primary/10 font-bold uppercase tracking-tight text-xs"
                onClick={handleDownloadQR}
              >
                <Download className="w-4 h-4" />
                Download QR Code
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
